import { useCallback } from 'react';

// Country class for IBAN data
class Country {
    constructor(name, code, bankForm, accForm) {
        this.name = name;
        this.code = code;
        this.bank = this.decodeFormat(bankForm);
        this.acc = this.decodeFormat(accForm);
        this.bank_lng = this.calcLength(this.bank);
        this.acc_lng = this.calcLength(this.acc);
        this.total_lng = 4 + this.bank_lng + this.acc_lng;
    }

    decodeFormat(form) {
        const formList = [];
        const parts = form.split(' ');
        for (let i = 0; i < parts.length; i++) {
            const part = parts[i];
            if (part !== '') {
                const typ = part.charAt(part.length - 1);
                const lng = parseInt(typ === 'a' || typ === 'n' ? part.substring(0, part.length - 1) : part);
                formList.push([lng, typ === 'a' || typ === 'n' ? typ : 'c']);
            }
        }
        return formList;
    }

    calcLength(formList) {
        return formList.reduce((sum, [lng]) => sum + lng, 0);
    }
}

// IBAN data from ISO 13616
const ibanData = [
    new Country("Andorra", "AD", "0  4n 4n", "0  12   0 "),
    new Country("Austria", "AT", "0  5n 0 ", "0  11n  0 "),
    new Country("Bosnia and Herzegovina", "BA", "0  3n 3n", "0   8n  2n"),
    new Country("Belgium", "BE", "0  3n 0 ", "0   7n  2n"),
    new Country("Bulgaria", "BG", "0  4a 4n", "2n  8   0 "),
    new Country("Switzerland", "CH", "0  5n 0 ", "0  12   0 "),
    new Country("Cyprus", "CY", "0  3n 5n", "0  16   0 "),
    new Country("Czech Republic", "CZ", "0  4n 0 ", "0  16n  0 "),
    new Country("Germany", "DE", "0  8n 0 ", "0  10n  0 "),
    new Country("Denmark", "DK", "0  4n 0 ", "0   9n  1n"),
    new Country("Estonia", "EE", "0  2n 0 ", "2n 11n  1n"),
    new Country("Spain", "ES", "0  4n 4n", "2n 10n  0 "),
    new Country("Finland", "FI", "0  6n 0 ", "0   7n  1n"),
    new Country("Faroe Islands", "FO", "0  4n 0 ", "0   9n  1n"),
    new Country("France", "FR", "0  5n 5n", "0  11   2n"),
    new Country("United Kingdom", "GB", "0  4a 6n", "0   8n  0 "),
    new Country("Gibraltar", "GI", "0  4a 0 ", "0  15   0 "),
    new Country("Greenland", "GL", "0  4n 0 ", "0   9n  1n"),
    new Country("Greece", "GR", "0  3n 4n", "0  16   0 "),
    new Country("Croatia", "HR", "0  7n 0 ", "0  10n  0 "),
    new Country("Hungary", "HU", "0  3n 4n", "1n 15n  1n"),
    new Country("Ireland", "IE", "0  4a 6n", "0   8n  0 "),
    new Country("Israel", "IL", "0  3n 3n", "0  13n  0 "),
    new Country("Iceland", "IS", "0  4n 0 ", "2n 16n  0 "),
    new Country("Italy", "IT", "1a 5n 5n", "0  12   0 "),
    new Country("Liechtenstein", "LI", "0  5n 0 ", "0  12   0 "),
    new Country("Lithuania", "LT", "0  5n 0 ", "0  11n  0 "),
    new Country("Luxembourg", "LU", "0  3n 0 ", "0  13   0 "),
    new Country("Latvia", "LV", "0  4a 0 ", "0  13   0 "),
    new Country("Monaco", "MC", "0  5n 5n", "0  11   2n"),
    new Country("Montenegro", "ME", "0  3n 0 ", "0  13n  2n"),
    new Country("Macedonia, Former Yugoslav Republic of", "MK", "0  3n 0 ", "0  10   2n"),
    new Country("Malta", "MT", "0  4a 5n", "0  18   0 "),
    new Country("Mauritius", "MU", "0  4a 4n", "0  15n  3a"),
    new Country("Netherlands", "NL", "0  4a 0 ", "0  10n  0 "),
    new Country("Norway", "NO", "0  4n 0 ", "0   6n  1n"),
    new Country("Poland", "PL", "0  8n 0 ", "0  16n  0 "),
    new Country("Portugal", "PT", "0  4n 4n", "0  11n  2n"),
    new Country("Romania", "RO", "0  4a 0 ", "0  16   0 "),
    new Country("Serbia", "RS", "0  3n 0 ", "0  13n  2n"),
    new Country("Saudi Arabia", "SA", "0  2n 0 ", "0  18   0 "),
    new Country("Sweden", "SE", "0  3n 0 ", "0  16n  1n"),
    new Country("Slovenia", "SI", "0  5n 0 ", "0   8n  2n"),
    new Country("Slovak Republic", "SK", "0  4n 0 ", "0  16n  0 "),
    new Country("San Marino", "SM", "1a 5n 5n", "0  12   0 "),
    new Country("Tunisia", "TN", "0  2n 3n", "0  13n  2n"),
    new Country("Turkey", "TR", "0  5n 0 ", "1  16   0 ")
];

const useIbanValidation = () => {
    const intern = useCallback((extern) => {
        if (extern.substring(0, 4) === "IBAN") {
            extern = extern.substring(4);
        }
        return extern.replace(/\s/g, '');
    }, []);

    const getCountryData = useCallback((code) => {
        return ibanData.find(country => country.code === code) || null;
    }, []);

    const invalidIBANlength = useCallback((country, iban) => {
        return iban.length !== country.total_lng;
    }, []);

    const invalidPart = useCallback((formList, ibanPart) => {
        for (let f = 0; f < formList.length; f++) {
            let [lng, typ] = formList[f];
            if (lng > ibanPart.length) {
                lng = ibanPart.length;
            }
            for (let i = 0; i < lng; i++) {
                const ch = ibanPart.charAt(i);
                const a = "A" <= ch && ch <= "Z";
                const n = "0" <= ch && ch <= "9";
                const c = n || a || ("a" <= ch && ch <= "z");
                if ((!c && typ === "c") || (!a && typ === "a") || (!n && typ === "n")) {
                    return true;
                }
            }
            ibanPart = ibanPart.substring(lng);
        }
        return false;
    }, []);

    const invalidBank = useCallback((country, bank) => {
        return bank.length !== country.bank_lng || invalidPart(country.bank, bank);
    }, [invalidPart]);

    const fill0 = useCallback((s, l) => {
        while (s.length < l) {
            s = "0" + s;
        }
        return s;
    }, []);

    const fillAccount = useCallback((country, account) => {
        return fill0(account, country.acc_lng);
    }, [fill0]);

    const invalidAccount = useCallback((country, account) => {
        return account.length < 1 || account.length > country.acc_lng || 
               invalidPart(country.acc, fillAccount(country, account));
    }, [invalidPart, fillAccount]);

    const capital2digits = useCallback((ch) => {
        const capitals = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
        return capitals.indexOf(ch) + 10;
    }, []);

    const mod97 = useCallback((digitString) => {
        let m = 0;
        for (let i = 0; i < digitString.length; i++) {
            m = (m * 10 + parseInt(digitString.charAt(i))) % 97;
        }
        return m;
    }, []);

    const checksumIBAN = useCallback((iban) => {
        const code = iban.substring(0, 2);
        const checksum = iban.substring(2, 4);
        const bban = iban.substring(4);

        let digits = "";
        for (let i = 0; i < bban.length; i++) {
            const ch = bban.charAt(i).toUpperCase();
            digits += "0" <= ch && ch <= "9" ? ch : capital2digits(ch);
        }
        for (let i = 0; i < code.length; i++) {
            digits += capital2digits(code.charAt(i));
        }
        digits += checksum;

        const checksumResult = 98 - mod97(digits);
        return fill0("" + checksumResult, 2);
    }, [capital2digits, mod97, fill0]);

    const ibanOkay = useCallback((iban) => {
        return checksumIBAN(iban) === "97";
    }, [checksumIBAN]);

    const validateIBAN = useCallback((iban, bic) => {
        
        const cleanIban = intern(iban);
        const code = cleanIban.substring(0, 2);
        const country = getCountryData(code);
     
        if (!country) {
            return {
                isValid: false,
                error: `Invalid country code: ${code}`
            };
        }

        // Special validation for Bulgarian IBANs
        if (code === 'BG' && bic) {
            const bankCode = cleanIban.substring(4, 8); // Get bank code from IBAN (STSA)
            const bicBankCode = bic.substring(0, 4); // Get bank code from BIC (STSA)
            
            
            if (bankCode !== bicBankCode) {
                return {
                    isValid: false,
                    error: 'Несъответствие между IBAN и BIC !'
                };
            }
        }

        if (invalidIBANlength(country, cleanIban)) {
            return {
                isValid: false,
                error: `Invalid IBAN length ${cleanIban.length} for ${country.name} (${country.total_lng})`
            };
        }

        const bank = cleanIban.substring(4, 4 + country.bank_lng);
        const account = cleanIban.substring(4 + country.bank_lng);


        if (invalidBank(country, bank)) {
            return {
                isValid: false,
                error: `Invalid bank code ${bank} for ${country.name}`
            };
        }

        if (invalidAccount(country, account)) {
            return {
                isValid: false,
                error: `Invalid account number ${account} for ${country.name}`
            };
        }

        if (!ibanOkay(cleanIban)) {
            return {
                isValid: false,
                error: 'Invalid IBAN checksum'
            };
        }

        return {
            isValid: true,
            error: null
        };
    }, [intern, getCountryData, invalidIBANlength, invalidBank, invalidAccount, ibanOkay]);

    return {
        validateIBAN
    };
};

export default useIbanValidation; 